// src/services/api.js
import axios from 'axios';

const api = axios.create({
    baseURL: process.env.REACT_APP_API_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

// ---------------------------------------------------------------------------
// Interceptor de Requisição
// Anexa o token JWT a todas as requisições autenticadas.
// ---------------------------------------------------------------------------
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

// ---------------------------------------------------------------------------
// Interceptor de Resposta
//
// REGRA IMPORTANTE: rotas de autenticação (/auth/login, /auth/forgot-password)
// NUNCA devem ser interceptadas para redirect — os erros 401/403 delas
// precisam chegar no catch() do componente para exibir a mensagem correta
// na tela de login. Redirecionar nesse momento causa o efeito de "piscar"
// ou "não acontecer nada" que o usuário vê.
// ---------------------------------------------------------------------------
const AUTH_ROUTES = ['/auth/login', '/auth/forgot-password', '/auth/register'];

const isAuthRoute = (config) => {
    if (!config?.url) return false;
    return AUTH_ROUTES.some(route => config.url.includes(route));
};

api.interceptors.response.use(
    // ── Sucesso ────────────────────────────────────────────────────────────
    (response) => {
        // Se o backend emitiu um token renovado (ex: após empresa ser deletada),
        // atualiza o localStorage silenciosamente.
        const newToken = response.headers['x-new-auth-token'];
        if (newToken) {
            console.log("Axios Interceptor: Novo token recebido. Atualizando localStorage.");
            localStorage.setItem('token', newToken);
        }
        return response;
    },

    // ── Erro ───────────────────────────────────────────────────────────────
    (error) => {
        const status    = error.response?.status;
        const data      = error.response?.data;
        const reqConfig = error.config;

        // Rotas de autenticação: nunca redireciona, deixa o erro chegar
        // no catch() do componente (LoginPage, etc.)
        if (isAuthRoute(reqConfig)) {
            return Promise.reject(error);
        }

        // 403 — Conta do usuário desativada
        if (status === 403 && data?.errorCode === 'ACCOUNT_INACTIVE') {
            console.log("Axios Interceptor: Conta inativa. Redirecionando para /conta-desativada.");
            localStorage.removeItem('token');
            window.location.href = '/conta-desativada';
            return Promise.reject(error);
        }

        // 403 — Empresa do usuário foi excluída
        if (status === 403 && data?.action === 'company_deleted_redirect_setup') {
            console.log("Axios Interceptor: Empresa excluída. Redirecionando para /organizacao/setup.");
            localStorage.removeItem('token');
            window.location.href = '/organizacao/setup';
            return Promise.reject(error);
        }

        // 403 — Usuário singular tentando acessar rota que exige empresa
        // Não redireciona globalmente — cada componente decide o que mostrar.
        if (status === 403 && data?.errorCode === 'SINGULAR_NO_COMPANY') {
            console.log("Axios Interceptor: Usuário singular sem empresa. Deixando o componente tratar.");
            return Promise.reject(error);
        }

        // 401 — Token inválido ou expirado (qualquer rota não-auth)
        // Só redireciona se não houver token (sessão encerrada de verdade).
        // Se o token ainda existe, pode ser uma chamada em paralelo — deixa o
        // componente ou o AuthContext tratar via revalidateAuth.
        if (status === 401) {
            const token = localStorage.getItem('token');
            if (token) {
                // Token existe mas o servidor rejeitou — provavelmente expirou.
                console.log("Axios Interceptor: Token rejeitado pelo servidor (401). Limpando e redirecionando.");
                localStorage.removeItem('token');
                window.location.href = '/login';
            }
            return Promise.reject(error);
        }

        // Qualquer outro erro: repassa para o catch() do componente
        return Promise.reject(error);
    }
);

export default api;